import React, { useState } from 'react';
import * as styles from './Header.module.css';
import { Reveal } from '../Animations/Reveal';
import { useLanguage } from '../../i18n';

// Assets
// Assuming webpack/vite handles png imports. If not, paths might differ.
// import darkThemeLogo from '../../assets/dark_theme_logo.png';
const darkThemeLogo = '/assets/logo/white_theme_logo.png';
// For imports that might not exist in typings, we might fallback to string path if needed, 
// but using the existing pattern.
const ACCOUNT_ICON = '/assets/icons/general_icons/account.svg';
const LOGO_WHITE = '/assets/logo/Icon_logo_White.png';

export const Header: React.FC = () => {
    const { language, setLanguage, t } = useLanguage();
    const [isLangOpen, setIsLangOpen] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    const toggleLang = () => setIsLangOpen(!isLangOpen);

    const openMobile = () => {
        setIsMobileOpen(true);
        setIsClosing(false);
    };

    const closeMobile = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsMobileOpen(false);
            setIsClosing(false);
        }, 300); // Match animation duration
    };

    const toggleMobile = () => {
        if (isMobileOpen) {
            closeMobile();
        } else {
            openMobile();
        }
    };

    return (
        <>
            <header className={styles.headerContainer} style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)' }}>
                {/* Mobile Toggle */}
                <Reveal direction="top" delay={0.1}>
                    <div className={styles.mobileToggle} onClick={toggleMobile}>
                        <span className={styles.hamburgerLine}></span>
                        <span className={styles.hamburgerLine}></span>
                        <span className={styles.hamburgerLine}></span>
                    </div>
                </Reveal>

                {/* Left Container - 20% */}
                <div className={styles.leftContainer}>
                    <Reveal direction="top" delay={0.1}>
                        <img
                            src={darkThemeLogo}
                            alt="NikoVen"
                            className={styles.logo}
                            onClick={() => window.location.href = '/aw'}
                            style={{ cursor: 'pointer' }}
                        />
                    </Reveal>
                </div>

                {/* Right Container - 80% */}
                <div className={styles.rightContainer}>
                    {/* Internal Left: Navigation */}
                    <div className={styles.navSection}>
                        {[
                            { key: 'Trayectoria', label: t.nav.trajectory },
                            { key: 'Productos', label: t.nav.products },
                            { key: 'Contacto', label: t.nav.contact },
                            { key: 'Servicios', label: t.nav.services },
                            { key: 'Shop', label: 'Shop', href: '/aw/shop' }
                        ].map((item, index) => (
                            <Reveal key={item.key} direction="top" delay={0.2 + (index * 0.1)}>
                                <a
                                    className={styles.navLink}
                                    href={(item as any).href || '#'}
                                    onClick={(e) => {
                                        if ((item as any).href) {
                                            // Allow default navigation or force it if needed
                                            // Usually for SPA Link component is better, but here we use <a>
                                        } else {
                                            e.preventDefault();
                                        }
                                    }}
                                >
                                    {item.label}
                                </a>
                            </Reveal>
                        ))}
                    </div>

                    {/* Internal Right: Actions */}
                    <div className={styles.actionsSection}>
                        <Reveal direction="top" delay={0.6}>
                            <button
                                className={styles.joinButton}
                                onMouseMove={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const x = ((e.clientX - rect.left) / rect.width) * 100;
                                    const y = ((e.clientY - rect.top) / rect.height) * 100;
                                    e.currentTarget.style.setProperty('--mouse-x', `${x}%`);
                                    e.currentTarget.style.setProperty('--mouse-y', `${y}%`);
                                }}
                                onClick={() => window.location.href = '/aw/shop'}
                            >
                                <span>Shop</span>
                                <svg className={styles.joinIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                                    <line x1="3" y1="6" x2="21" y2="6" />
                                    <path d="M16 10a4 4 0 0 1-8 0" />
                                </svg>
                            </button>
                        </Reveal>

                        <Reveal direction="top" delay={0.7}>
                            <div
                                className={`${styles.langContainer} ${isLangOpen ? styles.langOpen : ''}`}
                                onClick={toggleLang}
                                onMouseEnter={() => setIsLangOpen(true)}
                                onMouseLeave={() => setIsLangOpen(false)}
                            >
                                <span className={styles.langText}>
                                    {language === 'en' ? 'US' : 'ES'}
                                </span>
                                <div className={styles.langDropdown}>
                                    <div
                                        className={styles.langOption}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setLanguage(language === 'es' ? 'en' : 'es');
                                            setIsLangOpen(false);
                                        }}
                                    >
                                        {language === 'es' ? 'US' : 'ES'}
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </header>

            {/* Mobile Sidebar & Overlay */}
            {isMobileOpen && (
                <>
                    <div
                        className={`${styles.mobileOverlay} ${isClosing ? styles.mobileOverlayClosing : ''}`}
                        onClick={closeMobile}
                    />
                    <div className={`${styles.mobileSidebar} ${isClosing ? styles.mobileSidebarClosing : ''}`}>
                        <img
                            src={LOGO_WHITE}
                            alt="NikoVen White"
                            className={styles.sidebarLogo}
                        />
                        {[
                            { key: 'Trayectoria', label: t.nav.trajectory },
                            { key: 'Productos', label: t.nav.products },
                            { key: 'Contacto', label: t.nav.contact },
                            { key: 'Servicios', label: t.nav.services }
                        ].map((item) => (
                            <a key={item.key} className={styles.navLink} style={{ fontSize: '70%' }}>{item.label}</a>
                        ))}
                        <button
                            className={styles.joinButtonLight}
                            style={{ marginTop: 'auto' }}
                            onMouseMove={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const x = ((e.clientX - rect.left) / rect.width) * 100;
                                const y = ((e.clientY - rect.top) / rect.height) * 100;
                                e.currentTarget.style.setProperty('--mouse-x', `${x}%`);
                                e.currentTarget.style.setProperty('--mouse-y', `${y}%`);
                            }}
                            onClick={() => window.location.href = '/aw/shop'}
                        >
                            <span>Shop</span>
                            <svg className={styles.joinIconDark} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                                <line x1="3" y1="6" x2="21" y2="6" />
                                <path d="M16 10a4 4 0 0 1-8 0" />
                            </svg>
                        </button>
                    </div>
                </>
            )}
        </>
    );
};
